import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activateWarehouse,
  createWarehouse,
  deleteWarehouse,
  listWarehouses,
  purgeWarehouse,
  reorderWarehouses,
  updateWarehouse,
  type WarehouseListParams,
  type WarehouseListResult,
} from './api';
import type { Warehouse, WarehouseCreateInput, WarehouseUpdateInput } from './types';

const KEY = 'warehouses' as const;

/**
 * `enabled: false` holds the query until the caller knows its facility scope,
 * so an operational selector never briefly lists warehouses from every
 * facility — the backend would reject those choices on submit.
 */
export const useWarehousesList = (
  params: WarehouseListParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<WarehouseListResult, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listWarehouses(params),
    enabled: options.enabled ?? true,
    placeholderData: (prev) => prev,
  });
};

export const useCreateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<Warehouse, ApiClientError, WarehouseCreateInput>({
    mutationFn: (body) => createWarehouse(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useUpdateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<Warehouse, ApiClientError, { id: string; body: WarehouseUpdateInput }>({
    mutationFn: ({ id, body }) => updateWarehouse(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeleteWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => deleteWarehouse(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeWarehouse(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useReorderWarehouses = () => {
  const qc = useQueryClient();
  return useMutation<Warehouse[], ApiClientError, string[]>({
    mutationFn: (orderedIds) => reorderWarehouses(orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<Warehouse, ApiClientError, string>({
    mutationFn: (id) => activateWarehouse(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
